/**
 * Estado efímero del challenge MFA interno para sobrevivir remount en la misma pestaña.
 * No guardar OTP ni tokens. TTL corto.
 */

const SNAPSHOT_KEY = 'rc_internal_mfa_challenge_snap';
const RESTORE_PREP_KEY = 'rc_internal_mfa_restore_prepare_timestamps';

export type InternalMfaChallengeSnapshot = {
  userId: string;
  factorId: string;
  challengeId: string;
  preparedAt: number;
};

const TTL_MS = 15 * 60 * 1000;

const RESTORE_PREP_WINDOW_MS = 90_000;
const RESTORE_PREP_MAX = 2;

const RESTORE_COOLDOWN_UNTIL_KEY = 'rc_internal_mfa_restore_cooldown_until';

export function setRestoreCooldownMs(durationMs: number): void {
  try {
    sessionStorage.setItem(RESTORE_COOLDOWN_UNTIL_KEY, String(Date.now() + durationMs));
  } catch {
    // ignore
  }
}

export function isRestoreCooldownActive(): boolean {
  try {
    const until = Number(sessionStorage.getItem(RESTORE_COOLDOWN_UNTIL_KEY) || '0');
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

export function clearRestoreCooldown(): void {
  try {
    sessionStorage.removeItem(RESTORE_COOLDOWN_UNTIL_KEY);
  } catch {
    // ignore
  }
}

export function saveInternalMfaChallengeSnapshot(snap: InternalMfaChallengeSnapshot): void {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    // ignore quota / private mode
  }
}

export function readInternalMfaChallengeSnapshot(): InternalMfaChallengeSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InternalMfaChallengeSnapshot;
    if (
      typeof parsed?.userId !== 'string'
      || typeof parsed?.factorId !== 'string'
      || typeof parsed?.challengeId !== 'string'
      || typeof parsed?.preparedAt !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearInternalMfaChallengeSnapshot(): void {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

export function isChallengeSnapshotFresh(snap: InternalMfaChallengeSnapshot): boolean {
  return Date.now() - snap.preparedAt < TTL_MS;
}

export function hasFreshChallengeSnapshotForHydration(): boolean {
  const s = readInternalMfaChallengeSnapshot();
  return !!(s && isChallengeSnapshotFresh(s));
}

/** Limita prepares disparados solo por restore/remount (no login normal ni inspect). */
export function allowRestoreChallengePrepare(): { ok: true } | { ok: false; reason: string } {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(RESTORE_PREP_KEY);
    let timestamps: number[] = raw ? (JSON.parse(raw) as number[]) : [];
    timestamps = timestamps.filter((t) => typeof t === 'number' && now - t < RESTORE_PREP_WINDOW_MS);
    if (timestamps.length >= RESTORE_PREP_MAX) {
      return { ok: false, reason: 'restore_prepare_window_cap' };
    }
    timestamps.push(now);
    sessionStorage.setItem(RESTORE_PREP_KEY, JSON.stringify(timestamps));
    return { ok: true };
  } catch {
    return { ok: true };
  }
}

export function clearRestoreChallengePrepareHistory(): void {
  try {
    sessionStorage.removeItem(RESTORE_PREP_KEY);
  } catch {
    // ignore
  }
}

export function resetInternalMfaSessionArtifacts(): void {
  clearInternalMfaChallengeSnapshot();
  clearRestoreChallengePrepareHistory();
  clearRestoreCooldown();
}
